/**
 * HitlQueue (I1 · Figma 126:203) — the instructor escalation queue.
 *
 * Layout:
 *   - Filter chips (All / Assigned to me / Waiting > 5min)
 *   - Table of escalations with wait-time, severity, learner name/email,
 *     source lecture, "Take" button
 *   - WS subscription refreshes on hitl:queue:updated so accepting from
 *     another tab / instructor updates this view within a few hundred ms
 */

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { hitlApi, Escalation } from '../../services/hitlApi';
import { wsService } from '../../services/websocket';
import { fetchCurrentUserId } from '../../services/apiClient';
import { styles, formatDuration, formatTime, HITL_STATUS_ALERT, HITL_STATUS_WARN } from './HitlStyles';

type Filter = 'all' | 'assigned' | 'waiting_gt=5';

function severityColor(s: string): string {
  if (s === 'high') return HITL_STATUS_ALERT;
  if (s === 'medium') return HITL_STATUS_WARN;
  return '#71717A';
}

const HitlQueue: React.FC = () => {
  const nav = useNavigate();
  const [filter, setFilter] = useState<Filter>('all');
  const [escalations, setEscalations] = useState<Escalation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [takingId, setTakingId] = useState<string | null>(null);
  const requestGeneration = React.useRef(0);

  const refresh = React.useCallback(async () => {
    const generation = ++requestGeneration.current;
    setLoading(true);
    setError(null);
    try {
      const list = await hitlApi.listEscalations(filter);
      if (generation !== requestGeneration.current) return;
      setEscalations(list);
    } catch (err) {
      if (generation !== requestGeneration.current) return;
      const msg = err instanceof Error ? err.message : 'Failed to load queue';
      setError(msg);
    } finally {
      if (generation === requestGeneration.current) setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    void refresh();
    return () => { requestGeneration.current += 1; };
  }, [refresh]);

  // Live queue updates via WS. The server broadcasts hitl:queue:updated to
  // role:instructor whenever an escalation is requested / taken / resolved.
  useEffect(() => {
    const off = wsService.on<unknown>('hitl:queue:updated', () => {
      void refresh();
    });
    return () => off?.();
  }, [refresh]);

  // Polling fallback — Socket.IO can miss the delta if the tab was
  // suspended. 15s polling is cheap.
  useEffect(() => {
    const id = setInterval(refresh, 15_000);
    return () => clearInterval(id);
  }, [refresh]);

  const now = Date.now();

  // Resolve the logged-in instructor's backend DB user id (matches
  // escalation.instructor_id). This is NOT the OIDC sub — the backend keys
  // hitl_sessions.instructor_id on the internal users.id (req.user.userId).
  const [myId, setMyId] = useState('');
  useEffect(() => {
    let alive = true;
    void fetchCurrentUserId().then((id) => {
      if (alive) setMyId(id);
    });
    return () => {
      alive = false;
    };
  }, []);

  const handleTake = async (escalationId: string) => {
    setTakingId(escalationId);
    try {
      const { session } = await hitlApi.takeEscalation(escalationId);
      nav(`/hitl/session/${session.id}`);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to take escalation';
      setError(msg);
    } finally {
      setTakingId(null);
    }
  };

  return (
    <div style={styles.page}>
      <h1 style={styles.h1}>Escalation queue</h1>
      <p style={styles.subtitle}>
        Learners who have asked for a human. Take an escalation to open a live session.
      </p>

      <div style={{ ...styles.row, marginBottom: 24 }}>
        {(['all', 'assigned', 'waiting_gt=5'] as Filter[]).map((f) => (
          <button
            type="button"
            key={f}
            onClick={() => setFilter(f)}
            style={{
              ...styles.button,
              ...styles.secondary,
              ...(filter === f ? { background: '#F5E6D8', border: '1px solid #B8600B', color: '#B8600B' } : {}),
            }}
          >
            {f === 'all' && 'All'}
            {f === 'assigned' && 'Assigned to me'}
            {f === 'waiting_gt=5' && 'Waiting > 5 min'}
          </button>
        ))}
        <div style={{ marginLeft: 'auto' }}>
          <span style={styles.muted}>
            {error ? 'Queue status unavailable' : loading ? 'Checking queue…' : `${escalations.length} ${escalations.length === 1 ? 'escalation' : 'escalations'}`}
          </span>
        </div>
      </div>

      {error && (
        <div
          role="alert"
          style={{ ...styles.card, background: '#FEF2F2', borderColor: '#FCA5A5', color: '#B91C1C' }}
        >
          {error}
          <button type="button" onClick={() => void refresh()}>Retry queue</button>
        </div>
      )}

      {loading && <div style={styles.card}>Loading queue…</div>}

      {!loading && !error && escalations.length === 0 && (
        <div style={styles.card}>
          <div style={{ padding: 24, textAlign: 'center' }}>
            <div style={{ fontSize: 32, marginBottom: 8 }} aria-hidden>
              ✓
            </div>
            <div style={{ fontWeight: 500 }}>Queue clear</div>
            <div style={styles.muted}>No escalations waiting.</div>
          </div>
        </div>
      )}

      {!loading && !error && escalations.map((esc) => {
        const requestedMs = Date.parse(esc.requested_at);
        const waitMs = esc.wait_time_ms ?? now - requestedMs;
        // "Mine" means *this* instructor already took it — compare the
        // escalation's instructor_id against the logged-in user's DB id. A
        // session owned by another instructor is not mine (previously any
        // escalation with a session_id was mislabelled as the viewer's).
        const isMine = !!esc.session_id && !!myId && esc.instructor_id === myId;
        return (
          <div key={esc.id} style={styles.card}>
            <div style={styles.row}>
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 4 }}>
                  <span
                    style={{
                      ...styles.amberChip,
                      background: severityColor(esc.severity) + '22',
                      color: severityColor(esc.severity),
                    }}
                  >
                    {esc.severity.toUpperCase()}
                  </span>
                  <span style={{ fontWeight: 500 }}>Learner {esc.learner_id.slice(0, 8)}…</span>
                  <span style={styles.muted}>· requested {formatTime(esc.requested_at)}</span>
                </div>
                {esc.learner_note && (
                  <div style={{ ...styles.muted, marginTop: 4, color: '#3F3F46' }}>
                    “{esc.learner_note}”
                  </div>
                )}
                {esc.source_lecture_id && (
                  <div style={{ ...styles.muted, marginTop: 4 }}>
                    Source: {esc.source_lecture_id}
                  </div>
                )}
              </div>
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontSize: 20, fontWeight: 600 }}>{formatDuration(waitMs)}</div>
                <div style={styles.muted}>wait</div>
              </div>
              {isMine ? (
                <button
                  style={{ ...styles.button, ...styles.primary }}
                  onClick={() => nav(`/hitl/session/${esc.session_id}`)}
                >
                  Open session
                </button>
              ) : (
                <button
                  disabled={takingId === esc.id}
                  style={{
                    ...styles.button,
                    ...styles.primary,
                    opacity: takingId === esc.id ? 0.6 : 1,
                  }}
                  onClick={() => handleTake(esc.id)}
                >
                  {takingId === esc.id ? 'Taking…' : 'Take'}
                </button>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default HitlQueue;
